
/**
 * Media Service
 * Handles content discovery, search, and media details API calls
 */

import { apiRequest } from './api';

export const MediaService = {
    /**
     * Search for movies and TV shows
     * @param {string} query - Search query
     * @param {number} page - Page number (default: 1)
     * @returns {Promise<Object>}
     */
    async search(query, page = 1) {
        return apiRequest('/media/search', {
            params: { q: query, page },
        });
    },

    /**
     * Get trending content (TMDB)
     * @param {string} type - 'all', 'movie', or 'tv'
     * @param {number} page
     */
    async getTrending(type = 'all', page = 1) {
        return apiRequest('/media/trending', {
            params: { type, page },
        });
    },

    /**
     * Get Trending-to-Verify feed — titles ranked by recent Aluvo verification
     * activity, with a 4-tier cold-start fallback so the list is never empty.
     * Returns { results: [...] }
     */
    async getTrendingToVerify() {
        return apiRequest('/media/trending-to-verify');
    },

    async getCategories() {
        return apiRequest('/media/categories', {
            authenticated: false,
        });
    },

    async getCategory(categoryId, { kind = 'all', page = 1 } = {}) {
        return apiRequest(`/media/categories/${categoryId}`, {
            params: { kind, page },
        });
    },

    /**
     * Get detailed information about a movie or TV show
     * @param {string} kind - 'movie' or 'tv'
     * @param {number} tmdbId - TMDB ID
     * @param {number} seasonNumber - (Optional) Season number for TV shows
     * @returns {Promise<Object>}
     */
    async getDetails(kind, tmdbId, seasonNumber = null) {
        const params = seasonNumber ? { seasonNumber } : {};
        return apiRequest(`/media/details/${kind}/${tmdbId}`, {
            params,
        });
    },

    /**
     * Get all seasons for a TV show
     * @param {number} tmdbId - TMDB ID
     * @returns {Promise<Object>}
     */
    async getTvSeasons(tmdbId) {
        return apiRequest(`/media/tv/${tmdbId}/seasons`);
    },

    /**
     * Get details for a specific TV season
     * @param {number} tmdbId - TMDB ID
     * @param {number} seasonNumber - Season number
     * @returns {Promise<Object>}
     */
    async getTvSeasonDetails(tmdbId, seasonNumber) {
        return apiRequest(`/media/tv/${tmdbId}/seasons/${seasonNumber}`);
    },

    /**
     * Get streaming providers for a TV season
     * @param {number} tmdbId - TMDB ID
     * @param {number} seasonNumber - Season number
     * @param {string} locale - Country code (default: 'US')
     * @returns {Promise<Object>}
     */
    async getTvSeasonProviders(tmdbId, seasonNumber, locale = 'US') {
        return apiRequest(`/media/tv/${tmdbId}/seasons/${seasonNumber}/providers`, {
            params: { locale },
        });
    },

    /**
     * Get episodes for a specific TV season with coin info
     * @param {number} tmdbId - TMDB ID
     * @param {number} seasonNumber - Season number
     * @returns {Promise<Object>}
     */
    async getTvSeasonEpisodes(tmdbId, seasonNumber) {
        return apiRequest(`/media/tv/${tmdbId}/seasons/${seasonNumber}/episodes`);
    },

    /**
     * Get streaming providers for a movie
     * @param {number} tmdbId - TMDB ID
     * @param {string} locale - Country code (default: 'US')
     * @returns {Promise<Object>}
     */
    async getMovieProviders(tmdbId, locale = 'US') {
        return apiRequest(`/media/movie/${tmdbId}/providers`, {
            params: { locale },
        });
    },
};
